import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';

import {
  userSelector,
  isCorporateTASelector,
  isTeammateAdminSelector,
  isTeammateSelector,
} from 'redux/selectors/authenticationSelectors';
import SummarySection from './SummarySection';
import useQueryMatch from 'hooks/useQueryMatch';
import FinancialsSection from './FinancialsSection';
import { BackofficeUserModel } from 'api/backofficeApiClient';
import DashboardTabs, { DashboardTabsEnum } from './DashboardTabs';
import { useAppSelector } from 'hooks/useAppSelector';
import ReportDateUpdater from './ReportDateUpdater';

const defaultActiveTab = DashboardTabsEnum.Summary;

const getPageSections = (
  user: BackofficeUserModel | null,
  isCorporateTA?: boolean,
  isTeammateAdmin?: boolean,
  isTeammate?: boolean,
) => {
  if (isCorporateTA || (isTeammate && !isTeammateAdmin)) {
    return [DashboardTabsEnum.Summary];
  }

  return [DashboardTabsEnum.Summary, DashboardTabsEnum.Financials];
};

export default function UserHomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const query = queryString.parse(location.search);
  const [activeTab, setActiveTab] = useState<DashboardTabsEnum>(
    query.section || defaultActiveTab,
  );
  const user = useAppSelector(userSelector);
  const isCorporateTA = useAppSelector(isCorporateTASelector);
  const isTeammateAdmin = useAppSelector(isTeammateAdminSelector);
  const isTeammate = useAppSelector(isTeammateSelector);
  const pageSectionMatch = useQueryMatch(
    getPageSections(user, isCorporateTA, isTeammateAdmin, isTeammate),
  );

  useEffect(() => {
    setActiveTab(query.section || defaultActiveTab);
  }, [query.section]);

  useEffect(() => {
    if (!pageSectionMatch) {
      navigate(`${location.pathname}?section=${defaultActiveTab}`, {
        replace: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSectionMatch]);

  const getSection = () => {
    switch (activeTab) {
      case DashboardTabsEnum.Preferred:
        return null;

      case DashboardTabsEnum.Financials:
        return <FinancialsSection />;

      case DashboardTabsEnum.Summary:
      default:
        return <SummarySection />;
    }
  };

  return (
    <div className="page-wrapper scroll-box">
      <div className="page-box page-holder page-inner">
        <div className="page-inner-container">
          <DashboardTabs activeTab={activeTab} />

          {activeTab === DashboardTabsEnum.Financials && <ReportDateUpdater />}

          {getSection()}
        </div>
      </div>
    </div>
  );
}
